import mediaManagerTemplateDataMapper from 'modules/core/domain/data-mappers/media-manager-template-data-mapper';
import mediaManagerTemplateGroupDataMapper from 'modules/manager/domain/data-mappers/media-manager-template-group-data-mapper';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import {
  COPY_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
  COPY_MEDIA_MANAGER_TEMPLATE_MUTATION,
  CREATE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
  CREATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
  DELETE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
  DELETE_MEDIA_MANAGER_TEMPLATE_MUTATION,
  MEDIA_MANAGER_PRESETS_QUERY,
  MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_QUERY,
  MEDIA_MANAGER_TEMPLATES_QUERY,
  MEDIA_MANAGER_TEMPLATE_GROUPS_QUERY,
  MEDIA_MANAGER_TEMPLATE_GROUP_QUERY,
  MEDIA_MANAGER_TEMPLATE_QUERY,
  MediaManagerTemplateGql,
  MediaManagerTemplateGroupGql,
  UPDATE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
  UPDATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
} from 'modules/manager/domain/graphql';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import {
  CopyMediaManagerTemplate,
  CreateMediaManagerTemplate,
  UpdateMediaManagerTemplate,
} from 'modules/core/domain/interfaces/supabase/media-manager/actions';
import { baseApi } from 'shared/api/base-api';
import type { Draft } from 'immer';
import type { AppDispatch, RootState } from 'store/store';

export const MEDIA_MANAGER_TEMPLATE_TAG = 'MediaManagerTemplate' as const;
export const MEDIA_MANAGER_TEMPLATE_GROUP_TAG = 'MediaManagerTemplateGroup' as const;
export const MEDIA_MANAGER_TEMPLATES_LIST_TAG = { type: MEDIA_MANAGER_TEMPLATE_TAG, id: 'ORG-LIST' };
export const MEDIA_MANAGER_PRESETS_LIST_TAG = { type: MEDIA_MANAGER_TEMPLATE_TAG, id: 'PRESET-LIST' };
export const MEDIA_MANAGER_TEMPLATE_GROUPS_LIST_TAG = { type: MEDIA_MANAGER_TEMPLATE_GROUP_TAG, id: 'ORG-LIST' };
export const MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_LIST_TAG = { type: MEDIA_MANAGER_TEMPLATE_GROUP_TAG, id: 'PRESET-LIST' };

function mapTemplates(rows: MediaManagerTemplateGql[] | null | undefined): MediaManagerTemplate[] {
  return (rows ?? []).map(mediaManagerTemplateDataMapper);
}

function mapGroups(rows: MediaManagerTemplateGroupGql[] | null | undefined): MediaManagerTemplateGroup[] {
  return (rows ?? []).map(mediaManagerTemplateGroupDataMapper);
}

interface TemplatesQueryArgs {
  tournamentId: number;
  sportType?: string;
  templateGroupId?: number;
}

export interface CreateMediaManagerTemplateGroupInput {
  name: string;
  tournamentId?: number | null;
}

export interface UpdateMediaManagerTemplateGroupInput {
  id: number;
  name?: string;
}

export interface CopyMediaManagerTemplateGroupInput {
  templateGroupId: number;
  tournamentId: number;
  name?: string;
}

export const mediaManagerApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    mediaManagerTemplates: build.query<MediaManagerTemplate[], TemplatesQueryArgs>({
      query: ({ tournamentId, sportType, templateGroupId }) => ({
        query: MEDIA_MANAGER_TEMPLATES_QUERY,
        variables: { tournamentId, sportType, templateGroupId },
      }),
      transformResponse: (raw: { mediaManagerTemplates?: MediaManagerTemplateGql[] }) => mapTemplates(raw.mediaManagerTemplates),
      providesTags: (result) => [
        MEDIA_MANAGER_TEMPLATES_LIST_TAG,
        ...(result ?? []).map((template) => ({ type: MEDIA_MANAGER_TEMPLATE_TAG, id: template.id })),
      ],
    }),
    mediaManagerPresets: build.query<MediaManagerTemplate[], { sportType?: string } | void>({
      query: (arg) => ({
        query: MEDIA_MANAGER_PRESETS_QUERY,
        variables: { sportType: arg && 'sportType' in arg ? arg.sportType : undefined },
      }),
      transformResponse: (raw: { mediaManagerPresets?: MediaManagerTemplateGql[] }) => mapTemplates(raw.mediaManagerPresets),
      providesTags: (result) => [
        MEDIA_MANAGER_PRESETS_LIST_TAG,
        ...(result ?? []).map((template) => ({ type: MEDIA_MANAGER_TEMPLATE_TAG, id: template.id })),
      ],
    }),
    mediaManagerTemplate: build.query<MediaManagerTemplate, number>({
      query: (id) => ({
        query: MEDIA_MANAGER_TEMPLATE_QUERY,
        variables: { id },
      }),
      transformResponse: (raw: { mediaManagerTemplate: MediaManagerTemplateGql }) =>
        mediaManagerTemplateDataMapper(raw.mediaManagerTemplate),
      providesTags: (_result, _error, id) => [{ type: MEDIA_MANAGER_TEMPLATE_TAG, id }],
    }),
    mediaManagerTemplateGroups: build.query<MediaManagerTemplateGroup[], { tournamentId: number }>({
      query: ({ tournamentId }) => ({
        query: MEDIA_MANAGER_TEMPLATE_GROUPS_QUERY,
        variables: { tournamentId },
      }),
      transformResponse: (raw: { mediaManagerTemplateGroups?: MediaManagerTemplateGroupGql[] }) =>
        mapGroups(raw.mediaManagerTemplateGroups),
      providesTags: (result) => [
        MEDIA_MANAGER_TEMPLATE_GROUPS_LIST_TAG,
        ...(result ?? []).map((group) => ({ type: MEDIA_MANAGER_TEMPLATE_GROUP_TAG, id: group.id })),
      ],
    }),
    mediaManagerPresetTemplateGroups: build.query<MediaManagerTemplateGroup[], void>({
      query: () => ({
        query: MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_QUERY,
      }),
      transformResponse: (raw: { mediaManagerPresetTemplateGroups?: MediaManagerTemplateGroupGql[] }) =>
        mapGroups(raw.mediaManagerPresetTemplateGroups),
      providesTags: (result) => [
        MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_LIST_TAG,
        ...(result ?? []).map((group) => ({ type: MEDIA_MANAGER_TEMPLATE_GROUP_TAG, id: group.id })),
      ],
    }),
    mediaManagerTemplateGroup: build.query<MediaManagerTemplateGroup, number>({
      query: (id) => ({
        query: MEDIA_MANAGER_TEMPLATE_GROUP_QUERY,
        variables: { id },
      }),
      transformResponse: (raw: { mediaManagerTemplateGroup: MediaManagerTemplateGroupGql | null }) => {
        if (!raw.mediaManagerTemplateGroup) {
          throw new Error('Template group not found');
        }
        return mediaManagerTemplateGroupDataMapper(raw.mediaManagerTemplateGroup);
      },
      providesTags: (_result, _error, id) => [{ type: MEDIA_MANAGER_TEMPLATE_GROUP_TAG, id }],
    }),
    createMediaManagerTemplate: build.mutation<MediaManagerTemplate, CreateMediaManagerTemplate>({
      query: (input) => ({
        query: CREATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { createMediaManagerTemplate: MediaManagerTemplateGql }) =>
        mediaManagerTemplateDataMapper(raw.createMediaManagerTemplate),
      invalidatesTags: (_result, _error, input) => [
        MEDIA_MANAGER_TEMPLATES_LIST_TAG,
        MEDIA_MANAGER_PRESETS_LIST_TAG,
        { type: MEDIA_MANAGER_TEMPLATE_GROUP_TAG, id: input.templateGroupId },
        MEDIA_MANAGER_TEMPLATE_GROUPS_LIST_TAG,
        MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_LIST_TAG,
      ],
    }),
    updateMediaManagerTemplate: build.mutation<MediaManagerTemplate, UpdateMediaManagerTemplate>({
      query: (input) => ({
        query: UPDATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { updateMediaManagerTemplate: MediaManagerTemplateGql }) =>
        mediaManagerTemplateDataMapper(raw.updateMediaManagerTemplate),
      invalidatesTags: (_result, _error, input) => [
        { type: MEDIA_MANAGER_TEMPLATE_TAG, id: input.id },
        MEDIA_MANAGER_TEMPLATES_LIST_TAG,
        MEDIA_MANAGER_PRESETS_LIST_TAG,
        MEDIA_MANAGER_TEMPLATE_GROUPS_LIST_TAG,
        MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_LIST_TAG,
        ...(input.templateGroupId != null ? [{ type: MEDIA_MANAGER_TEMPLATE_GROUP_TAG, id: input.templateGroupId }] : []),
      ],
    }),
    deleteMediaManagerTemplate: build.mutation<{ status: string }, number>({
      query: (id) => ({
        query: DELETE_MEDIA_MANAGER_TEMPLATE_MUTATION,
        variables: { id },
      }),
      transformResponse: (raw: { deleteMediaManagerTemplate: { status: string } }) => raw.deleteMediaManagerTemplate,
      invalidatesTags: (_result, _error, id) => [
        { type: MEDIA_MANAGER_TEMPLATE_TAG, id },
        MEDIA_MANAGER_TEMPLATES_LIST_TAG,
        MEDIA_MANAGER_PRESETS_LIST_TAG,
        MEDIA_MANAGER_TEMPLATE_GROUPS_LIST_TAG,
        MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_LIST_TAG,
      ],
    }),
    copyMediaManagerTemplate: build.mutation<MediaManagerTemplate, CopyMediaManagerTemplate>({
      query: (input) => ({
        query: COPY_MEDIA_MANAGER_TEMPLATE_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { copyMediaManagerTemplate: MediaManagerTemplateGql }) =>
        mediaManagerTemplateDataMapper(raw.copyMediaManagerTemplate),
    }),
    createMediaManagerTemplateGroup: build.mutation<MediaManagerTemplateGroup, CreateMediaManagerTemplateGroupInput>({
      query: (input) => ({
        query: CREATE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { createMediaManagerTemplateGroup: MediaManagerTemplateGroupGql }) =>
        mediaManagerTemplateGroupDataMapper(raw.createMediaManagerTemplateGroup),
      invalidatesTags: (_result, _error, input) =>
        input.tournamentId == null ? [MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_LIST_TAG] : [MEDIA_MANAGER_TEMPLATE_GROUPS_LIST_TAG],
    }),
    updateMediaManagerTemplateGroup: build.mutation<MediaManagerTemplateGroup, UpdateMediaManagerTemplateGroupInput>({
      query: (input) => ({
        query: UPDATE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { updateMediaManagerTemplateGroup: MediaManagerTemplateGroupGql }) =>
        mediaManagerTemplateGroupDataMapper(raw.updateMediaManagerTemplateGroup),
      invalidatesTags: (_result, _error, input) => [
        { type: MEDIA_MANAGER_TEMPLATE_GROUP_TAG, id: input.id },
        MEDIA_MANAGER_TEMPLATE_GROUPS_LIST_TAG,
        MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_LIST_TAG,
      ],
    }),
    deleteMediaManagerTemplateGroup: build.mutation<{ status: string }, { id: number; deleteTemplates?: boolean }>({
      query: ({ id, deleteTemplates }) => ({
        query: DELETE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
        variables: { id, deleteTemplates },
      }),
      transformResponse: (raw: { deleteMediaManagerTemplateGroup: { status: string } }) => raw.deleteMediaManagerTemplateGroup,
      invalidatesTags: (_result, _error, { id }) => [
        { type: MEDIA_MANAGER_TEMPLATE_GROUP_TAG, id },
        MEDIA_MANAGER_TEMPLATE_GROUPS_LIST_TAG,
        MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_LIST_TAG,
        MEDIA_MANAGER_TEMPLATES_LIST_TAG,
        MEDIA_MANAGER_PRESETS_LIST_TAG,
      ],
    }),
    copyMediaManagerTemplateGroup: build.mutation<MediaManagerTemplateGroup, CopyMediaManagerTemplateGroupInput>({
      query: (input) => ({
        query: COPY_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
        variables: { input },
      }),
      transformResponse: (raw: { copyMediaManagerTemplateGroup: MediaManagerTemplateGroupGql }) =>
        mediaManagerTemplateGroupDataMapper(raw.copyMediaManagerTemplateGroup),
      invalidatesTags: [MEDIA_MANAGER_TEMPLATE_GROUPS_LIST_TAG, MEDIA_MANAGER_TEMPLATES_LIST_TAG],
    }),
  }),
  overrideExisting: false,
});

/**
 * Puts a freshly created copy into every cached templates list it belongs to, so the
 * list shows it without a page reload and without an extra request. A copy always
 * lands in the group it was made for; a list filtered by sport takes it only
 * when the sport matches.
 */
export const upsertMediaManagerTemplateIntoCache =
  (copy: MediaManagerTemplate) => (dispatch: AppDispatch, getState: () => RootState) => {
    Object.values(getState().api.queries).forEach((entry) => {
      if (entry?.endpointName !== 'mediaManagerTemplates') return;

      const args = entry.originalArgs as TemplatesQueryArgs;
      if (args.tournamentId !== copy.tournamentId) return;
      if (args.templateGroupId != null && args.templateGroupId !== copy.templateGroupId) return;
      if (args.sportType && args.sportType !== copy.sportType) return;

      dispatch(
        mediaManagerApi.util.updateQueryData('mediaManagerTemplates', args, (draft) => {
          if (draft.some((template) => template.id === copy.id)) return;
          draft.push(copy as MediaManagerTemplate & Draft<MediaManagerTemplate>);
        }),
      );
    });
  };

export const {
  useMediaManagerTemplatesQuery,
  useMediaManagerPresetsQuery,
  useLazyMediaManagerTemplateQuery,
  useCreateMediaManagerTemplateMutation,
  useUpdateMediaManagerTemplateMutation,
  useDeleteMediaManagerTemplateMutation,
  useCopyMediaManagerTemplateMutation,
  useMediaManagerTemplateGroupsQuery,
  useMediaManagerPresetTemplateGroupsQuery,
  useMediaManagerTemplateGroupQuery,
  useCreateMediaManagerTemplateGroupMutation,
  useUpdateMediaManagerTemplateGroupMutation,
  useDeleteMediaManagerTemplateGroupMutation,
  useCopyMediaManagerTemplateGroupMutation,
} = mediaManagerApi;
